import { Dispatch, FormEvent, SetStateAction, useState } from 'react';

import { ChatTool } from '~/lib/chat';
import { isValidFunctionCallingJsonSchema } from '~/lib/jsonschema';

import { Combobox } from '../../combo-box';
import { Button } from '~/components/ui/Button';
import { Textarea } from '~/components/ui/textarea';

const exampleTools = {
  weather: {
    name: 'get_the_weather',
    description: 'Get the weather',
    parameters: {
      type: 'object',
      properties: {
        city: {
          type: 'string',
          description: 'The city to get the weather for',
        },
        state: {
          type: 'string',
          description: 'The state to get the weather for',
        },
      },
      required: ['city', 'state'],
    },
  },
  stocks: {
    name: 'get_stock_price',
    description: 'Get a stock price',
    parameters: {
      type: 'object',
      properties: {
        symbol: {
          type: 'string',
          description: 'The stock symbol to get the price for',
        },
      },
      required: ['symbol'],
    },
  },
};

const exampleFunctionSchema = JSON.stringify(
  {
    name: 'chat',
    description: 'Chat with the model. The text to send to the model to generate a response and continue the chat',
    parameters: {
      type: 'object',
      properties: {
        text: {
          type: 'string',
          description: 'The text to send to the model to generate a response and continue the chat',
        },
      },
      required: ['text'],
    },
  },
  null,
  2,
);

export interface FunctionCallingModalProps {
  jsonInput: string;
  setJsonInput: Dispatch<SetStateAction<string>>;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
  currentEditingFunction: ChatTool | null;
  handleDelete: (functionToDelete: string) => void;
  cancel: () => void;
}
export default function FunctionCallingForm({
  jsonInput,
  cancel,
  setJsonInput,
  handleSubmit,
  currentEditingFunction,
  handleDelete,
}: FunctionCallingModalProps) {
  const [isValidTool, setIsValidTool] = useState(true);

  const handleJsonInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const json = e.target.value;
    let parsedJson: any;
    try {
      parsedJson = JSON.parse(json);
    } catch {
      setIsValidTool(false);
    }
    if (isValidFunctionCallingJsonSchema(parsedJson)) {
      setIsValidTool(true);
    }
    setJsonInput(json);
  };
  return (
    <form
      className="grid items-start gap-4 w-full"
      onSubmit={
        isValidTool
          ? handleSubmit
          : () => {
              // eslint-disable-next-line no-alert
            }
      }
    >
      <div className="grid gap-2">
        <div className="flex flex-row justify-between gap-2 mt-2">
          <div>
            <label htmlFor="jsonInput" className="block text-sm mt-4 font-bold text-gray-700">
              JSON Schema
            </label>
          </div>
          <div className="w-3/5 flex justify-end content-end">
            <Combobox
              options={[
                {
                  label: 'Get the weather',
                  value: 'weather',
                },
                {
                  label: 'Get a stock price',
                  value: 'stocks',
                },
              ]}
              defaultMessage="Add an example function"
              emptyMessage="No examples found"
              onSelect={(value: any) => {
                const selectedTool = exampleTools[value as keyof typeof exampleTools];
                if (selectedTool) {
                  setJsonInput(JSON.stringify(selectedTool, null, 2));
                }
              }}
            />
          </div>
        </div>
        <Textarea
          id="jsonInput"
          value={jsonInput}
          onChange={handleJsonInputChange}
          rows={20}
          className="p-2 border rounded "
          placeholder={exampleFunctionSchema}
        />
      </div>
      <div className="flex flex-row justify-between gap-2">
        {currentEditingFunction ? (
          <Button
            type="button"
            onClick={() => handleDelete(currentEditingFunction.function.name)}
            variant="destructive"
          >
            Delete
          </Button>
        ) : (
          <Button
            variant={'outline'}
            onClick={() => {
              cancel();
            }}
            className="w-62"
          >
            Cancel
          </Button>
        )}
        <Button disabled={!isValidTool} type="submit">
          Save
        </Button>
      </div>
    </form>
  );
}
